/**
 * URL / domain display helpers: percent-decoding and punycode (IDN) → Unicode.
 * Used on the results URL list and unfixable-domain preview tables.
 */

import punycode from 'punycode/';

/**
 * Decode punycode (xn--) hostname labels to Unicode for display.
 */
export function decodeHostnameForDisplay(hostname: string): string {
    if (!hostname || typeof hostname !== 'string') return hostname;
    if (!hostname.toLowerCase().includes('xn--')) return hostname;

    return hostname
        .split('.')
        .map((label) => {
            const lower = label.toLowerCase();
            if (!lower.startsWith('xn--')) return label;
            try {
                return punycode.decode(lower.slice(4));
            } catch {
                return label;
            }
        })
        .join('.');
}

/**
 * Decode a stored domain (ACE/punycode) for UI display.
 */
export function decodeDomainForDisplay(domain: string): string {
    if (!domain || typeof domain !== 'string') return domain;
    const trimmed = domain.trim();
    if (!trimmed) return domain;
    return decodeHostnameForDisplay(trimmed);
}

/**
 * Encode a Unicode domain label to punycode (ACE) for storage / matching.
 */
export function encodeDomainForStorage(domain: string): string {
    if (!domain || typeof domain !== 'string') return domain;
    const trimmed = domain.trim();
    if (!trimmed) return domain;

    return trimmed
        .split('.')
        .map((label) => {
            let isAscii = true;
            for (let i = 0; i < label.length; i++) {
                if (label.charCodeAt(i) > 127) {
                    isAscii = false;
                    break;
                }
            }
            if (!label || isAscii) {
                return label.toLowerCase();
            }
            try {
                return `xn--${punycode.encode(label.toLowerCase())}`;
            } catch {
                return label.toLowerCase();
            }
        })
        .join('.');
}

/**
 * Decode percent-encoding in a URL so path/query segments show readable characters
 * (e.g. Japanese) instead of %e4%bf%ae... in the UI.
 * Also decodes punycode hostnames to Unicode (IDN), matching browser URL list display.
 */
export function decodeUrlForDisplay(url: string): string {
    if (!url || typeof url !== 'string') return url;
    const trimmed = url.trim();
    if (!trimmed) return url;

    let decoded: string;
    try {
        decoded = decodeURI(trimmed);
    } catch {
        try {
            decoded = decodeURIComponent(trimmed);
        } catch {
            decoded = trimmed;
        }
    }

    try {
        const withScheme = /^https?:\/\//i.test(decoded) ? decoded : `https://${decoded}`;
        const parsed = new URL(withScheme);
        const displayHost = decodeHostnameForDisplay(parsed.hostname);
        if (displayHost && displayHost !== parsed.hostname) {
            decoded = decoded.replace(parsed.hostname, displayHost);
        }
    } catch {
        // Not a URL — leave decoded string as-is
    }

    return decoded;
}
